import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { StoryModel } from './models/story.model';
import { plainToClass } from 'class-transformer';
import { map, flatMap, catchError } from 'rxjs/operators';
import { StoryChunkLoadModel, StoryLoadConfigModel } from './state/story.state.model';
import { ChapterService } from './chapter.service';
import { ChapterModel } from './models/chapter.model';
import {
  StoryTranslationModel,
  StoryTranslationMetaInfo,
  StoryTranslationQuizQuestionModel,
  StoryTranslationMarkdownBlock,
} from './models/story-translation.model';
import { SceneGraphNode } from '../edit/models/scene-graph-node';
import { LabelModel } from './models/labels/label.model';
import { ContentType } from './models/content-type.enum';
import { flatten } from 'lodash';
import { EngineService } from '../engine/engine.service';
import { makeAsync } from '../shared/make-async';
import { uuid } from '../shared/uuid';
import { ColorUtils } from '../shared/libraries/colors/colors';
import { environment } from 'src/environments/environment';
import {
  LabelQuestion,
  MultipleChoiceQuestion,
  QuizLabelOption,
  QuizOption,
  QuizQuestion,
  SelectAnatomyQuestion,
} from '../quiz/models/quiz-question.model';
import { ContentBlocksService } from '../content-blocks/content-blocks.service';
import { questionTypesMap, QuizQuestionType } from '../quiz/models/quiz-question-type.enum';
import { MarkdownBlock } from '../content-blocks/models/markdown-block';
import { HumanInterface } from '../engine/models/human.interface';

declare const Human: HumanInterface;
declare const localBaseDirectory: string;

/**
 * These can be broken up for progressive loading
 */
const globalTypes = ['reflections', 'lights']; // Not bound to objects
const staticTypes = ['material', 'geometry', 'transform']; // Needed for render
const dynamicTypes = ['tweens', 'morph']; // Needed to animate

@Injectable({
  providedIn: 'root',
})
export class StoryService {
  constructor(
    private http: HttpClient,
    private engineService: EngineService,
    private chapterService: ChapterService,
    private contentBlocksService: ContentBlocksService,
  ) {}

  // get the object ids to be shown in the specified human chapter
  private getShownObjectsFromChapter(humanChapter: any): string[] {
    const synchronziation: { [key: string]: any } = humanChapter.synchronization;
    const objectIds: string[] = [];
    Object.entries(synchronziation).forEach(([objectId, sync]) => {
      if (sync.shown) {
        objectIds.push(objectId);
      }
    });
    return objectIds;
  }

  // chunk.activate needs to be set to false for either
  // Create a single load chunk per chapter, to ensure animation and models load before interactivity
  private getChapterLoadChunks(
    storyId: string,
    humanChapter: any,
    config: StoryLoadConfigModel,
  ): StoryChunkLoadModel[] {
    const chapterObjects = this.getShownObjectsFromChapter(humanChapter);
    const chunk = new StoryChunkLoadModel();
    chunk.activate = false;
    chunk.chapterId = humanChapter.id;
    chunk.objects = chapterObjects;
    chunk.storyId = storyId;
    // queue up static/dynamic assets for joint loading
    chunk.assetTypes = [...staticTypes, ...dynamicTypes, ...globalTypes];
    chunk.readyState = { model: true, animation: true };

    return [chunk];
  }

  // create load chunks for on-demand loading
  private getOnDemandLoadChunks(
    storyId: string,
    config: StoryLoadConfigModel,
  ): StoryChunkLoadModel[] {
    let chunks: StoryChunkLoadModel[] = [];

    // create chunks for chapters according to story load configuration
    Human.timeline.rootTimelines[storyId]._chapters.forEach((humanChapter) => {
      const _chapterChunks = this.getChapterLoadChunks(storyId, humanChapter, config);

      if (humanChapter.id === config.targetChapterId) {
        // target chapter chunks go first
        chunks = [..._chapterChunks, ...chunks];
      } else {
        // add non-target chunks in order
        chunks = [...chunks, ..._chapterChunks];
      }
    });

    return chunks;
  }

  // get the index of the chapter in the story
  getChapterIndex(storyId: string, chapterId: string): number {
    if (Human.timeline.rootTimelines[storyId]) {
      const root = Human.timeline.rootTimelines[storyId];
      return root._chapters.findIndex((chapter) => chapter.id === chapterId);
    } else {
      return -1;
    }
  }

  // entry point to get either a module or bookmark
  getStory(id: string, type: ContentType, lang: string = 'en'): Observable<StoryModel> {
    /**
     * type: string => 'module' or 'bookmark'
     * id: string => id of content
     */

    // get content
    const story$ = type === ContentType.Bookmark ? this.getBookmark(id) : this.getContent(id);

    // default English
    if (lang === 'en') {
      return story$;
    } else {
      let story: StoryModel;
      // transform content into target language
      return story$
        .pipe(
          flatMap((_story) => {
            story = _story;
            return this.getStoryTranslation(id, type, lang);
          }),
        )
        .pipe(
          map((translation) => this.translateStory(story, translation)),
          catchError(() => story$),
        );
    }
  }

  /**
   * In offline mode window.localBaseDirectory will be set to the file path of the content
   * otherwise requests will be relative to '/'
   */
  private getContentRoot(): String {
    if (environment.offline === true) {
      return typeof localBaseDirectory === 'undefined' ? '' : localBaseDirectory;
    }
    return '/';
  }

  // get a biodigital module -> normalize chapter data
  getContent(path: string): Observable<StoryModel> {
    return this.http.get(this.getContentRoot() + `content/modules/${path}.json`).pipe(
      map((result: any) => {
        return result as StoryModel;
      }),
    );
  }

  // get a biodigital bookmark -> normalize chapter data
  getBookmark(id: string): Observable<StoryModel> {
    return this.http.get(`/search/bookmarks/data`, { params: { be: id } }).pipe(
      map((result: any) => {
        return result.bookmark as StoryModel;
      }),
    );
  }

  // create configuration for story loading
  getLoadConfig(storyId: string, targetChapterIdx: number = 0): StoryLoadConfigModel {
    const config = new StoryLoadConfigModel();
    config.chapterAssetBundle = true;
    config.targetChapterId = Human.timeline.rootTimelines[storyId]._chapters[targetChapterIdx].id;
    config.targetChapterConfig = {
      global: {
        clipping: true,
        graph: true,
        camera: true,
        properties: true,
        transparencyMode: true,
        postprocess: true,
      },
      static: { labels: true },
      dynamic: { timeline: true, particles: true },
    };
    config.sortBy = 'chapter';
    return config;
  }

  // Create the Human module
  // This is the entrypoint into the engine
  // when a new module arrives, it needs to be added to the existing story.
  buildStory(storyId: string, story: StoryModel, activate: boolean): Observable<boolean> {
    // To support offline loading, the engine needs to be configured to know how to build asset URLs.
    // Even in non-offline mode, this event sets the configuration -> this should be done with defaults
    // Grep the engine for 'Human.events.on("modules.fetch.start"'
    Human.events.fire('modules.fetch.start', storyId);

    Human.modules.moduleData[storyId] = story; // Place the JSON that into Human modules for local reference
    const moduleData = { ...story };
    moduleData.moduleId = storyId; // needed by engine

    return new Observable((observer) => {
      Human.modules.create(storyId, moduleData, () => {
        Human.modules.load(
          storyId,
          Object.values(Human.scene.rootObjects),
          [...globalTypes],
          () => {
            Human.modules.activate(storyId, 0, {}, () => {
              observer.next(true);
              observer.complete();
            });
          },
        );
      });
    });
  }

  getStoryChapters(storyId: string, storyType: string): ChapterModel[] {
    const humanModuleData = Human.modules.moduleData[storyId];

    return Human.timeline.rootTimelines[storyId]._chapters.map((humanChapter, idx) => {
      return this.chapterService.fromHumanChapter(humanModuleData, storyType, humanChapter, idx);
    });
  }

  // create list of load chunks for loading story content into engine
  getStoryLoadChunks(storyId: string, config: StoryLoadConfigModel): StoryChunkLoadModel[] {
    let chunks: StoryChunkLoadModel[] = [];
    chunks = this.getOnDemandLoadChunks(storyId, config);

    // give each chunk a unique ID.
    return chunks.map((chunk) => {
      return { ...chunk, chunkId: uuid.idWithPrefix('chunk') };
    });
  }

  // load story chunk into engine according to chunk configuration
  loadStoryChunk(chunk: StoryChunkLoadModel, ok: Function): void {
    const objects = chunk.objects.map((id) => Human.scene.objects[id]); // ids to objects

    if (!!chunk.storyId) {
      // story based loading
      Human.modules.load(chunk.storyId, objects, chunk.assetTypes, () => {
        ok();
      });
    } else {
      // object based loading
      this.engineService.loadGraphObject(chunk.objects[0]).subscribe(() => {
        ok();
      });
    }
  }

  // #TODO: Support multiple module ids
  unloadStory(id: string, purgeAssets: boolean = true): Observable<boolean> {
    const ids = id ? [id] : Object.keys(Human.modules.modules);
    const deletions$ = ids.map((_id) => this.hardDeleteStory(_id, purgeAssets));
    return new Observable((observer) => {
      if (deletions$.length) {
        forkJoin(deletions$).subscribe(() => {
          observer.next(true);
          observer.complete();
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  // create a load chunk for loading a objects
  createLoadObjectChunk(id: string): StoryChunkLoadModel {
    const chunk = new StoryChunkLoadModel();
    chunk.assetTypes = [...staticTypes, ...dynamicTypes];
    chunk.objects = [id];
    return chunk;
  }

  private hardDeleteStory(id: string, purgeAssets: boolean = true): Observable<boolean> {
    const assetTypes = [
      'geometries',
      'morphs',
      'reflections',
      'tweens',
      'lights',
      'materials',
      'transforms',
    ];
    const storyGraph = id in Human.modules.modules ? Human.modules.modules[id].sceneGraph : [];
    const storyRoots = storyGraph
      .map((object: any) => Human.scene.objects[object.objectId])
      .filter((obj) => !!obj);
    const storyObjects = flatten(storyRoots.map((root) => root.getLeafObjects()));

    const deactivate = makeAsync(Human.modules.deactivate, id, {});
    const unload = makeAsync(Human.modules.unload, id, storyObjects, assetTypes);
    const destroy = makeAsync(Human.modules.destroy, id);

    return new Observable((observer) => {
      function endObserver() {
        observer.next();
        observer.complete();
      }

      // #TODO: [EM] Engine bug, never calls deactivate callback if target module is not currently active.
      const shouldDeactivate = Human.timeline.activeRoot && Human.timeline.activeRoot.id === id;

      (shouldDeactivate ? deactivate() : Promise.resolve({}))
        .then(() => (purgeAssets ? unload() : Promise.resolve({})))
        .then(() => destroy())
        .then(() => endObserver());
    });
  }

  /**
   * Gets the translation data for the specified content.
   */
  getStoryTranslation(
    id: string,
    type: ContentType,
    lang: string,
  ): Observable<StoryTranslationModel> {
    // translation data endpoint
    const url =
      type === ContentType.Bookmark
        ? `/search/bookmarks/tr/${lang}/${id}`
        : `/tr/${lang}/${id}.json`;

    return this.http.get(url).pipe(
      map((result: any) => {
        return plainToClass(StoryTranslationModel, result as Object);
      }),
    );
  }

  /**
   * Check the requested id to determine if requested content is a bookmark or story
   */
  getStoryType(id: string): ContentType {
    return id.indexOf('/') >= 0 ? ContentType.Module : ContentType.Bookmark;
  }

  /**
   * Translate the specified story based on the input translations.
   */
  translateStory(story: StoryModel, translation: StoryTranslationModel): StoryModel {
    const chapters = story.chapters || [];
    const sceneGraph = story.sceneGraph || [];
    const translatedObjects = translation.objects || {};
    const translatedChapters = translation.chapters || [];

    // Translate story title + description
    story.displayName = translation.displayName || story.displayName;
    story.description = translation.description || story.description;

    // Translate scene graph
    sceneGraph.forEach((graph) => {
      const objects = graph.objects || [];
      objects.forEach((object: SceneGraphNode) => {
        this.translateObject(object, translatedObjects);
      });
    });

    // Translate chapters
    chapters.forEach((chapter, chapterIndex) => {
      const translatedChapter = translatedChapters[chapterIndex];
      if (translatedChapter) {
        this.translateMetaInfo(chapter, translatedChapter);

        // Translate annotations
        const annotations = chapter.annotations || [];
        const translatedAnnotations = translatedChapter.annotations || [];
        annotations.forEach((annotation, annotationIndex) => {
          const translatedAnnotation = translatedAnnotations[annotationIndex];
          if (translatedAnnotation) {
            this.translateMetaInfo(annotation, translatedAnnotation);
          }
        });

        // Translate hotspots
        const hotspots = chapter.hotspots || [];
        const translatedHotspots = translatedChapter.hotspots || [];
        hotspots.forEach((hotspot, hotspotIndex) => {
          const translatedHotspot = translatedHotspots[hotspotIndex];
          if (translatedHotspot) {
            this.translateMetaInfo(hotspot, translatedHotspot);
          }
        });

        // Translate Quiz Questions
        const quizQuestions = chapter.quizQuestions || [];
        const translatedQuizQuestions = translatedChapter.quizQuestions || [];
        quizQuestions.forEach((quizQuestion, quizQuestionIndex) => {
          const translatedQuizQuestion = translatedQuizQuestions[quizQuestionIndex];
          if (translatedQuizQuestion) {
            this.translateQuizQuestion(quizQuestion, translatedQuizQuestion);
          }
        });
      }
    });

    // #TODO[AC]: translate global hotspots

    return story;
  }

  translateQuizQuestion(
    quizQuestion: QuizQuestion,
    translatedQuizQuestion: StoryTranslationQuizQuestionModel,
  ) {
    if (translatedQuizQuestion.nextLabel) {
      quizQuestion.nextLabel = translatedQuizQuestion.nextLabel;
    }

    ['questionPrompt', 'explanation'].forEach((property) => {
      const blocks = quizQuestion[property] || [];
      const translatedBlocks = translatedQuizQuestion[property] || [];

      blocks.forEach((block: MarkdownBlock, index) => {
        const translatedBlock = translatedBlocks[index];

        if (translatedBlock) {
          block.markdown = translatedBlock.markdown;
        }
      });
    });

    if (quizQuestion.data && translatedQuizQuestion.data) {
      const questionOptions = quizQuestion.data.options || [];
      const translatedQuestionOptions = translatedQuizQuestion.data.options || [];

      questionOptions.forEach((questionOption, index) => {
        const translatedQuestionOption = translatedQuestionOptions[index];
        if (translatedQuestionOption) {
          if (quizQuestion.type === QuizQuestionType.LabelAnatomy) {
            this.translateMetaInfo(
              questionOption.label,
              translatedQuestionOption.label as StoryTranslationMetaInfo,
            );
          } else {
            questionOption.label = translatedQuestionOption.label;
          }
        }
      });
    }
  }

  translateMetaInfo(state: any, metaInfo: StoryTranslationMetaInfo) {
    const { title, description } = metaInfo;
    // translate title
    if (state.hasOwnProperty('displayName')) {
      state.displayName = title;
    }
    if (state.hasOwnProperty('title')) {
      state.title = title;
    }
    // translate description
    if (state.hasOwnProperty('description')) {
      state.description = description;
    }
  }

  translateObject(object: SceneGraphNode, translatedObjects: { [key: string]: string }) {
    const objectId = object.objectId;
    const translatedDisplayName = translatedObjects[objectId];
    // Translate object
    if (translatedDisplayName) {
      object.displayName = translatedDisplayName;
    }
    // Translate children
    const childObjects = object.objects;
    if (childObjects) {
      childObjects.forEach((childObject: SceneGraphNode) => {
        this.translateObject(childObject, translatedObjects);
      });
    }
  }

  /**
   * Generate a text file from the written content
   */
  exportWrittenContent(story: StoryModel, chapters: ChapterModel[], labels: LabelModel[]) {
    const output = [];

    // Story title
    output.push(`
${story.displayName}
    `);

    // Chapter
    chapters.forEach((chapter, index) => {
      // Chapter content
      output.push(`
---

# Chapter ${index + 1}: ${chapter.info.displayName || '-No Title-'}

${chapter.info.description || '-No Description-'}
      `);

      // Chapter legend (if present)
      if (chapter.legend.length) {
        output.push(`
## Legend
`);
        chapter.legend.forEach((chapterLegend) => {
          output.push(`
* ${chapterLegend.label} (${ColorUtils.rgbToHex(chapterLegend.colors, true)})
`);
        });
      }

      // Chapter labels
      const chapterLabels = labels.filter((label) => label.chapterId === chapter.id);
      if (chapterLabels.length) {
        output.push(`
## Labels
`);

        chapterLabels.forEach((chapterLabel) => {
          output.push(`
* ${chapterLabel.title}
`);

          if (chapterLabel.description) {
            output.push(`
  > ${chapterLabel.description}
`);
          }
        });
      }
    });

    return output.join('');
  }

  exportQuizKey(questions: QuizQuestion[]): string {
    const output = [];

    questions.forEach((question, questionIndex) => {
      output.push(`
# QUESTION ${questionIndex + 1} (${questionTypesMap[question.type]})

## PROMPT
${this.contentBlocksService.getOnlyText(question.questionPrompt)}
      `);

      switch (question.type) {
        // Multiple Choice
        case QuizQuestionType.MultipleChoice:
          (question as MultipleChoiceQuestion).data.options.forEach((option, answerIndex) => {
            output.push(`
## ANSWER ${answerIndex + 1} ${
              (question as MultipleChoiceQuestion).data.correctAnswer === option.value
                ? '- CORRECT'
                : ''
            }
${option.label}
            `);
          });
          break;

        // Select Anatomy
        case QuizQuestionType.SelectAnatomy:
          output.push(`
## ANSWERS
          `);

          (question as SelectAnatomyQuestion).data.correctAnswer.forEach((option, answerIndex) => {
            output.push(`
${option}
            `);
          });
          break;

        // Label Anatomy
        case QuizQuestionType.LabelAnatomy:
          (question as LabelQuestion).data.options.forEach((option, answerIndex) => {
            output.push(`
## ANSWER ${answerIndex + 1}
${option.label.title}
            `);
          });
      }

      if (this.contentBlocksService.getOnlyText(question.explanation).length) {
        output.push(`
  
## EXPLANATION
${this.contentBlocksService.getOnlyText(question.explanation)}
        `);
      }

      output.push(`


      `);
    });

    return output.join('');
  }
}
